import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";
import { CompanyTeasers } from "../components/CompanyTeasers";
import { Link } from "react-router-dom";
import { ArrowUpRight, Dna, Rocket, Handshake } from "@phosphor-icons/react";

const Hl = ({ children, dark = false }) => (
  <span className={`font-semibold underline decoration-accent decoration-2 underline-offset-4 ${dark ? "text-white" : "text-primary"}`}>{children}</span>
);

const AI_DIFFERENCE = [
  { num: "01", title: "Lower cost, higher quality", text: "Fewer teams, better output — AI does what layered delivery teams used to, so cost falls without quality following it down." },
  { num: "02", title: "Efficiency by default", text: "Intelligence is built into the workflow, not bolted on." },
  { num: "03", title: "Compliance built in", text: "Auditable, reproducible, explainable by design." },
  { num: "04", title: "Fast, not elephant-slow", text: "Legacy vendors bolt AI onto old systems and crawl. We're AI-native and lean, so we move at the speed care needs." },
  { num: "05", title: "Savings passed on", text: "Because we don't need enormous margins, efficiencies reach our partners and their patients." },
];

const OFFERS = [
  { icon: Dna, title: "Rare disease? Almost free.", text: "Patients waiting on the edges shouldn't be held hostage by enterprise pricing. We refuse to play that game — our solutions are almost free for rare diseases." },
  { icon: Rocket, title: "Not in market, not earning? We're free.", text: "If your pharma product isn't in the market and isn't generating revenue yet, we work free — free credits to start, alongside you all the way to Go-to-Market at bare-minimum cost. You focus on the science. We'll carry the tech." },
  { icon: Handshake, title: "Partnership, not invoices", text: "We tie ourselves to your journey, not your invoice. The deeper you go with us, the bigger your edge — and the further ahead of the elephants you get." },
];

export default function About() {
  return (
    <>
      <PageHeader
        testId="about-page"
        overline="About Anxya Tech"
        title={["Stop feeding elephants.", "Move at the speed of AI."]}
        subtitle="Lean. Agile. AI-native. Fast and cost-effective — almost free for rare diseases, and free for partners not yet in market. We're a completely new kind of healthcare company, built for a world that can't afford the slow, heavy, expensive way anymore."
      />

      <section className="pb-10 lg:pb-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp delay={0.5}>
            <div className="overflow-hidden rounded-3xl border border-primary/10" data-testid="about-elephant-visual">
              <img
                src="/assets/elephant-ai.png"
                alt="The elephant of legacy IT dissolving into a lightning-fast AI network"
                className="w-full object-cover"
                loading="lazy"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <FadeUp>
              <div className="overflow-hidden rounded-3xl border border-primary/10 aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1600&q=80"
                  alt="Anxya Tech team collaborating"
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-6">
            <FadeUp>
              <Overline>Who we are</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
                We're not an elephant. And that's the point.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Anxya Tech is a <Hl>completely new-age, AI-based company</Hl> — an unconventional healthcare products, solutions and services company. Traditional IT service providers and product companies are <Hl>elephants: huge, slow, costly, and painful to move</Hl>. Everything we build is leveraged on advanced AI platforms — which makes our solutions <Hl>lean, cost-effective, compliant, and secure by design</Hl>.
              </p>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Our customer support, maintenance, updates, and user experience are all AI-based — <Hl>lightning fast, end to end</Hl>. Our philosophy is <Hl>not crony capitalism</Hl>: it is to improve patient experience and care while bringing healthcare costs down.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <CompanyTeasers />

      <section className="py-24 lg:py-32 bg-primary text-white" data-testid="about-company">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-2">
          <FadeUp>
            <Overline>About the company</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight">
              Self-sustaining. Self-reliant. Un-corporate by choice.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed">
              Honestly? <Hl dark>Enormous profit and revenue are not our goal.</Hl> Because AI carries so much of our work, we stay <Hl dark>lean and independent</Hl> — no dependence on constant fundraising or aggressive margins to survive. That freedom is deliberate: it lets us decide in the <Hl dark>interest of patients and partners, not investors</Hl>. Our purpose is simple — bring the cost of IT solutions and services down, and quality, efficiency, and compliance up.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Overline>Founders' philosophy</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight">
              Non-conventional. Non-capitalist. Non-centralised.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed">
              Our founders don't run a conventional company, because they don't see the world through a conventional lens — non-capitalist in spirit, decentralised by design, with no growth for its own sake. Our founder is a <Hl dark>yogi who lives from abundance, not scarcity</Hl> — and has <Hl dark>dedicated the rest of his life to society and community</Hl>. When you already feel abundant, profit stops being the point. We don't do conventional part-time philanthropy or CSR — <Hl dark>it's ingrained in our DNA</Hl>, and the company is built on these principles. What matters is what the work makes possible: better care, healthier lives, and dignity returned to the people healthcare exists to serve.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white border-b border-primary/10" data-testid="about-ai-difference">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>The difference AI makes</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
              Lower cost. Higher quality. Real compliance.
            </h2>
          </FadeUp>
          <div className="mt-16 space-y-0 border-t border-primary/10">
            {AI_DIFFERENCE.map((v, i) => (
              <FadeUp key={v.num} delay={i * 0.05}>
                <div className="grid gap-4 lg:grid-cols-12 py-8 border-b border-primary/10 items-baseline">
                  <span className="lg:col-span-2 font-display text-5xl font-light text-primary/15">{v.num}</span>
                  <h3 className="lg:col-span-4 font-display text-2xl font-medium tracking-tight text-foreground">{v.title}</h3>
                  <p className="lg:col-span-6 text-base text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="about-offers">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>For pharma, biotech and life sciences</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary max-w-3xl">
              The part the elephants will never offer you
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {OFFERS.map((o, i) => (
              <FadeUp key={o.title} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-2xl border border-primary/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:shadow-xl hover:shadow-accent/10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/5 text-primary">
                    <o.icon size={24} weight="duotone" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-foreground">{o.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{o.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25} className="mt-12">
            <div className="rounded-2xl border border-accent/30 bg-accent/5 p-8 lg:p-10">
              <h3 className="font-display text-2xl font-medium tracking-tight text-primary">The more you engage, the more you gain.</h3>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-3xl">
                Every workflow you bring us makes our AI smarter about your business — so every next engagement is faster, cheaper, and more precisely yours than the last. Elephants get slower and pricier as they grow. We get faster and cheaper as we go.
              </p>
              <Link
                to="/contact"
                data-testid="about-free-credits-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
              >
                Claim your free credits <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="max-w-3xl">
              <Overline>Why we exist</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
                To make care better — and lives lighter
              </h2>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
                We want people to receive better care, live fuller lives, and be treated with dignity by a system that costs less and works better. If our AI lets a hospital <Hl>spend less on IT and more on patients</Hl> — we've succeeded. Our end goal is simple: improve patient experience and care, and contribute significantly towards <Hl>improving our collective health index</Hl>. <Hl>Profit was never the measure. This is.</Hl>
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="rounded-3xl bg-primary p-10 lg:p-16 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <Overline>Lean. Fast. Compliant. Almost free where it matters most.</Overline>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-xl">
                  A different kind of healthcare company. Come build it with us.
                </h2>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/careers"
                  data-testid="about-cta-roles-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                >
                  See Open Roles <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/demo"
                  data-testid="about-cta-talk-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
                >
                  Talk to Us
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
